import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@/shared/components/toast";
import type IAutomationService from "../contracts/IAutomationService";
import AutomationService from "../services/AutomationService";
import type ExecutionStatus from "../models/ExecutionStatus";

const automationService: IAutomationService = new AutomationService();

const TERMINAL_STATES = new Set(["Completed", "Failed", "Cancelled", "Suspended"]);
const POLL_INTERVAL_MS = 1500;

export function useWorkflowExecution() {
  const [status, setStatus] = useState<ExecutionStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  const run = useCallback(
    async (
      workflowId: string,
      triggerPayload?: Record<string, unknown>,
    ): Promise<ExecutionStatus> => {
      stopPolling();
      setStatus(null);

      const initial = await automationService.Execute(workflowId, triggerPayload);
      setStatus(initial);

      if (TERMINAL_STATES.has(initial.status)) return initial;

      setPolling(true);
      return new Promise<ExecutionStatus>((resolve, reject) => {
        timerRef.current = setInterval(async () => {
          try {
            const next = await automationService.GetExecutionStatus(
              workflowId,
              initial.executionId,
            );
            setStatus(next);
            if (TERMINAL_STATES.has(next.status)) {
              stopPolling();
              resolve(next);
            }
          } catch (err) {
            stopPolling();
            const error = err instanceof Error ? err : new Error(String(err));
            toast.error(error.message);
            reject(error);
          }
        }, POLL_INTERVAL_MS);
      });
    },
    [stopPolling],
  );

  return { status, polling, run, reset: stopPolling };
}
